// TGP-PATCH(local-transport): Keeps local gateway builds away from browser state left by Telegram builds.
// The app's session and cache keys are shared by both modes, so a local build runs only on an origin that it
// claimed while the origin had no stored state. Existing state is detected by key and database names only;
// values are never read and nothing is removed or migrated.
import { LOCAL_TRANSPORT_WS_URL } from '../config';

export const LOCAL_ORIGIN_MARKER_KEY = 'tgp-local-transport-origin';
const LOCAL_ORIGIN_MARKER_VALUE = '1';
const BLOCKED_MESSAGE = 'Local transport build stopped: this origin holds state from another build. '
  + 'Serve the local build from a separate origin (host or port) or a fresh browser profile.';

type ListStorageNames = () => Promise<string[]>;

// The app is imported only after this check resolves in `src/index.tsx`
export const LOCAL_ORIGIN_CHECK = LOCAL_TRANSPORT_WS_URL ? guardLocalOrigin() : undefined;

function guardLocalOrigin() {
  try {
    return claimLocalOrigin(localStorage, listOriginStorageNames).then(assertNoServiceWorkers).catch((err: unknown) => {
      renderBlockedMessage();
      throw err;
    });
  } catch (err) {
    renderBlockedMessage();
    throw err;
  }
}

async function assertNoServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  if (registrations.length || navigator.serviceWorker.controller) {
    throw new Error(BLOCKED_MESSAGE);
  }
}

/**
 * Throws synchronously when `localStorage` has keys on an unclaimed origin. Otherwise resolves once the origin is
 * claimed, or rejects when IndexedDB, Cache Storage or Service Worker state is present.
 */
export function claimLocalOrigin(storage: Storage, listStorageNames: ListStorageNames) {
  if (storage.getItem(LOCAL_ORIGIN_MARKER_KEY) === LOCAL_ORIGIN_MARKER_VALUE) return Promise.resolve();

  if (storage.length) {
    throw new Error(BLOCKED_MESSAGE);
  }

  // Started synchronously, so the snapshot precedes storage created by this page
  return listStorageNames().then((names) => {
    if (names.length) {
      throw new Error(BLOCKED_MESSAGE);
    }

    storage.setItem(LOCAL_ORIGIN_MARKER_KEY, LOCAL_ORIGIN_MARKER_VALUE);
  });
}

// TGP-PATCH(local-transport): Account reset must retain the origin's provenance;
// clearing IndexedDB records leaves database names that an unclaimed origin rejects.
export function clearAccountLocalStorage(storage: Storage, prefix: string) {
  Object.keys(storage)
    .filter((key) => key.startsWith(prefix) && (!prefix || key !== LOCAL_ORIGIN_MARKER_KEY))
    .forEach((key) => storage.removeItem(key));
}

// Rejects when `indexedDB.databases` is unsupported, failing closed
async function listOriginStorageNames() {
  const [databases, cacheNames, registrations] = await Promise.all([
    indexedDB.databases(),
    'caches' in self ? caches.keys() : [],
    'serviceWorker' in navigator ? navigator.serviceWorker.getRegistrations() : [],
  ]);

  return [
    ...databases.map(({ name }) => name || ''),
    ...cacheNames,
    ...registrations.map(({ scope }) => scope),
  ];
}

function renderBlockedMessage() {
  document.body.textContent = BLOCKED_MESSAGE;
}
